import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Fuse from 'fuse.js'
import { useDocumentStore, type Document } from '@/lib/stores/documentStore'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { cn } from '@/lib/utils'
import { API_BASE_URL } from '@/lib/services/api'
import { DOCUMENTS_ENDPOINTS } from '@/api/endpoints'
import {
  Folder,
  FileText,
  ExternalLink,
  ChevronRight,
  Home,
  Search,
  X,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface FileBrowserProps {
  className?: string
}

const fuseOptions = {
  keys: ['name', 'path', 'folderPath'],
  threshold: 0.3,
  minMatchCharLength: 2,
}

export function FileBrowser({ className }: FileBrowserProps) {
  const { t } = useTranslation()
  const { activeCrisis } = useCrisisStore()
  const { getDocumentsByFolder, documents, isLoading, error } = useDocumentStore()

  const [items, setItems] = useState<Document[]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<Document[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const loadFolder = useCallback(
    async (path: string) => {
      if (!activeCrisis) return
      const docs = await getDocumentsByFolder(String(activeCrisis.id), path)
      setItems(docs)
    },
    [activeCrisis, getDocumentsByFolder]
  )

  // Load root on mount / crisis change
  useEffect(() => {
    setBreadcrumbs([])
    loadFolder('')
    setSearchTerm('')
    setIsSearching(false)
  }, [activeCrisis]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFolderClick = async (folder: Document) => {
    setBreadcrumbs((prev) => [...prev, folder])
    await loadFolder(folder.path)
  }

  const handleBreadcrumbClick = async (index: number) => {
    if (index === -1) {
      setBreadcrumbs([])
      await loadFolder('')
    } else {
      const next = breadcrumbs.slice(0, index + 1)
      setBreadcrumbs(next)
      await loadFolder(next[next.length - 1].path)
    }
  }

  // Fuzzy search across all cached documents (client-side, instant)
  const fuse = useMemo(() => {
    return new Fuse(documents, fuseOptions)
  }, [documents])

  const searchResults = useMemo(() => {
    if (searchTerm.length === 0) return []
    const results = fuse.search(searchTerm)
    // Filter out folders from search results
    return results.map((result) => result.item).filter((doc) => !doc.isFolder)
  }, [fuse, searchTerm])

  // Handle search - just enable search mode, no API calls
  const handleSearch = (term: string) => {
    setSearchTerm(term)
    if (term.length > 0) {
      setIsSearching(true)
    } else {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchTerm('')
    setIsSearching(false)
    searchInputRef.current?.focus()
  }

  const downloadDocument = (doc: Document) => {
    setDownloadingDocId(doc.id)
    const csrfCookie = document.cookie
      .split('; ')
      .find((c) => c.startsWith('csrf_access_token='))
    const csrfToken = csrfCookie ? csrfCookie.split('=')[1] : null

    fetch(`${API_BASE_URL}${DOCUMENTS_ENDPOINTS.DOWNLOAD(doc.id)}`, {
      credentials: 'include',
      headers: csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error('Download failed')
        return res.blob()
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = Object.assign(document.createElement('a'), {
          href: url,
          download: doc.name,
        })
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
      .catch((err) => console.error('Document download error:', err))
      .finally(() => setDownloadingDocId(null))
  }

  const folders = items.filter((i) => i.isFolder)
  const files = items.filter((i) => !i.isFolder)

  // Build location label from folderPath: segments from index 2 onwards joined with " / "
  const getLocationPath = (folderPath: string): string | null => {
    const parts = folderPath.split('/').filter(Boolean)
    const relevant = parts.slice(2)
    return relevant.length > 0 ? relevant.join(' / ') : null
  }

  const formatSize = (bytes?: number): string => {
    if (!bytes) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Search bar */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={t('documents.search')}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                clearSearch()
              }
            }}
            className="pl-10"
            disabled={isLoading}
          />
        </div>
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className="px-2"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Search results view */}
      {isSearching && searchTerm.length > 0 && (
        <>
          {searchResults.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {t('documents.noResults')}
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="px-4 py-2 font-medium">Naam</th>
                    <th className="px-4 py-2 font-medium hidden sm:table-cell">Gewijzigd</th>
                    <th className="px-4 py-2 font-medium hidden md:table-cell text-right">Grootte</th>
                    <th className="px-4 py-2 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {searchResults.map((file) => (
                    <tr key={file.id} className="hover:bg-gray-50 transition-colors group cursor-pointer" onClick={() => !downloadingDocId && downloadDocument(file)}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          {downloadingDocId === file.id ? (
                            <Loader2 className="w-4 h-4 text-gray-400 shrink-0 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                          )}
                          <div className="min-w-0">
                            {getLocationPath(file.folderPath ?? file.path) && (
                              <div className="mb-0.5 flex items-center gap-1 text-xs text-blue-600">
                                <span>in</span>
                                <Folder className="w-3 h-3 shrink-0" />
                                <span className="truncate">{getLocationPath(file.folderPath ?? file.path)}</span>
                              </div>
                            )}
                            <span className="text-foreground truncate block">{file.name}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden sm:table-cell">
                        {formatDate(file.lastModified)}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell text-right">
                        {formatSize(file.size)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {file.webUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('documents.openInSharePoint')}
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(file.webUrl, '_blank', 'noopener,noreferrer')
                            }}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-2">
            {searchResults.length} bestand{searchResults.length !== 1 ? 'en' : ''}
          </p>
        </>
      )}

      {/* Folder view */}
      {!isSearching && (
        <>
          {/* Breadcrumb */}
          <nav
            aria-label="Mapnavigatie"
            className="flex items-center gap-1 text-sm text-muted-foreground mb-3 flex-wrap min-h-[28px]"
          >
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className={cn(
                'p-1 rounded hover:bg-gray-100 hover:text-foreground transition-colors',
                breadcrumbs.length === 0 && 'text-foreground'
              )}
              aria-label="Startmap"
              title="Startmap"
            >
              <Home className="w-4 h-4" />
            </button>
            {breadcrumbs.map((crumb, i) => (
              <div key={crumb.id} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3 shrink-0 text-gray-300" />
                <button
                  onClick={() => handleBreadcrumbClick(i)}
                  className={cn(
                    'px-1 py-0.5 rounded hover:bg-gray-100 hover:text-foreground transition-colors truncate max-w-48',
                    i === breadcrumbs.length - 1 && 'text-foreground font-medium'
                  )}
                  title={crumb.name}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </nav>
        </>
      )}

      {/* File list - only show when not searching */}
      {!isSearching && (
        <>
          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{t('documents.loading')}</span>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            {items.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {t('documents.noDocuments')}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="px-4 py-2 font-medium">Naam</th>
                    <th className="px-4 py-2 font-medium hidden sm:table-cell">Gewijzigd</th>
                    <th className="px-4 py-2 font-medium hidden md:table-cell text-right">Grootte</th>
                    <th className="px-4 py-2 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {/* Folders first */}
                  {folders.map((folder) => (
                    <tr
                      key={folder.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors group"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <Folder className="w-4 h-4 text-blue-500 shrink-0" />
                          <span className="font-medium text-foreground group-hover:text-blue-600 transition-colors">
                            {folder.name}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-300 shrink-0 ml-auto" />
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden sm:table-cell">
                        {formatDate(folder.lastModified)}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell text-right">
                        —
                      </td>
                      <td className="px-4 py-2.5" />
                    </tr>
                  ))}

                  {/* Files */}
                  {files.map((file) => (
                    <tr
                      key={file.id}
                      className="hover:bg-gray-50 transition-colors group cursor-pointer"
                      onClick={() => !downloadingDocId && downloadDocument(file)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          {downloadingDocId === file.id ? (
                            <Loader2 className="w-4 h-4 text-gray-400 shrink-0 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                          )}
                          <span className="text-foreground truncate">{file.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden sm:table-cell">
                        {formatDate(file.lastModified)}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell text-right">
                        {formatSize(file.size)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {file.webUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('documents.openInSharePoint')}
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(
                                file.webUrl,
                                '_blank',
                                'noopener,noreferrer'
                              )
                            }}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Item count */}
          {!isLoading && items.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {folders.length > 0 &&
                `${folders.length} map${folders.length !== 1 ? 'pen' : ''}`}
              {folders.length > 0 && files.length > 0 && ', '}
              {files.length > 0 &&
                `${files.length} bestand${files.length !== 1 ? 'en' : ''}`}
            </p>
          )}
        </>
      )}
    </div>
  )
}
